import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Heart, Clock, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

// URLs atualizadas de /s320/ para /s1200/ para alta definição
const SLIDER_IMAGES = [
  "https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEhSozqzpcv2NY1bYef06Tq4PWZRBh37C4AXSuuQLje6N3YW9XSBY9rv2tGLUf3OUYWRKLlNy5qtYBDjyZGl6LAcKcSaabCY0Z5Z7pRk-Rx1JNpYIVceZ5sKk0YOPfMm07vMBhFZXCdyMUjmMBzGZ_m5WZ-FllM1V96MtoLLo2UrpHoUoXUHYwoZGGQsHwxM/s1200/1.png",
  "https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEjkcjpEBAVEWv2exuI2L2NMk8f9frmkkkBTYEFh9UC6wWIVW8G6CvsUArVqSKyWHUxBT-yDJyqyX-dqjQPGRdGPnWiq9DQexZKFcYtCdmNcrrg_BiVz2nyDnhCSOs3mcApUzHG54-G0g6SGqQUnVs6zp9dWw14hTMHfssfntJLJpOC9CLwIaZaRp_rrI4qD/s1200/2.png",
  "https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEgmN6hnUc4DfH0Kc1tcbTCdV_mdI7RmG_R0frQ2Nr058vTYgLpi_dne2icKfanKGCXk7NFFBwh51O39hK4Tei33_cRzZ7FvVBwy0ngcxUdw39Fc4m-PATdN3VfW4qz1VpO8-xW4yuJ1KZf7rvoZkWl6xDktw5Il5sX1vlIIa9yY6JN1ahcHukD8gd9wQncX/s1200/3.png"
];

const WHATSAPP_NUMBER = "351913918967";

const features = [
  { icon: Star, label: '5,0 no Google', sublabel: '126 avaliações' },
  { icon: Heart, label: 'Muito carinho', sublabel: 'e paciência' },
  { icon: Clock, label: 'Atendimento', sublabel: 'personalizado' },
];

export const Hero = ({ onBookingClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Lógica do Slider Automático (3 segundos)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % SLIDER_IMAGES.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const openWhatsApp = () => {
    const message = encodeURIComponent("Olá Pet Guardiões! Gostaria de agendar um serviço para o meu patudo.");
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const url = isMobile 
      ? `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`
      : `https://web.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${message}`;
    window.open(url, '_blank');
  };

  const scrollToBooking = () => {
    const element = document.querySelector('#agendamento');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="inicio" 
      className="relative min-h-screen pt-20 md:pt-24 overflow-hidden paw-pattern"
    >
      <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-300/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-400/15 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* LADO ESQUERDO: TEXTO */}
          <motion.div
            className="text-left"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full mb-6">
              <Shield className="w-4 h-4" />
              <span className="text-sm font-medium">Empresa liderada por empreendedoras</span>
            </div>

            <h1 className="font-nunito text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
              Cuidamos do seu{' '}
              <span className="text-yellow-500 relative">
                patudo
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                  <path d="M2 10C50 2 150 2 198 10" stroke="#FFD700" strokeWidth="4" strokeLinecap="round"/>
                </svg>
              </span>
              {' '}com amor, calma e profissionalismo
            </h1>

            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-lg">
              Banho e tosquia em Setúbal, num ambiente seguro e acolhedor. 
              Cada animal é tratado como família.
            </p>

            {/* FEATURES */}
            <div className="flex flex-wrap gap-4 md:gap-6 mb-10">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 bg-white rounded-2xl px-4 py-3 shadow-lg border border-yellow-100">
                  <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center text-yellow-600">
                    <feature.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{feature.label}</p>
                    <p className="text-xs text-gray-500">{feature.sublabel}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={scrollToBooking} className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-lg px-8 py-6 rounded-full shadow-xl">
                Agendar agora
              </Button>
              <Button onClick={openWhatsApp} variant="outline" className="border-2 border-gray-200 text-gray-700 font-semibold text-lg px-8 py-6 rounded-full">
                Falar no WhatsApp
              </Button>
            </div>
          </motion.div>

          {/* LADO DIREITO: SLIDER DE IMAGENS */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-yellow-400 rounded-3xl -z-10 rotate-12" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-yellow-200 rounded-3xl -z-10 -rotate-12" />
            
            {/* Container do Slider */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gray-100 aspect-[4/5]">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentIndex}
                  src={SLIDER_IMAGES[currentIndex]}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>

              {/* Indicadores de bolinha (Dots) */}
              <div className="absolute top-4 right-4 flex gap-2">
                {SLIDER_IMAGES.map((_, idx) => (
                  <div 
                    key={idx}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentIndex ? 'bg-yellow-500 w-6' : 'bg-white/50'}`}
                  />
                ))}
              </div>
              
              {/* Badge de Avaliação */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-yellow-100 border-2 border-white flex items-center justify-center">
                        <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">+126 patudos felizes</p>
                    <p className="text-sm text-gray-500">Avaliação 5.0 no Google</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Patinha flutuante */}
            <motion.div
              className="absolute -top-4 right-10 text-4xl"
              animate={{ y: [0, -10, 0], rotate: [0, 10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              🐾
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};